import * as THREE from "three";
import { splice } from "../shader";
import { NEUTRAL_BUILD, type ArtStyle, type Surface } from "./types";

// Comic (#76): cel-shaded ink, Jet Set Radio / Borderlands vibes. Light comes
// in three hard bands instead of a gradient, the shadow band is printed with
// a halftone dot screen that gets denser as it darkens, a hard highlight
// pops on the brightest spots, colors are pushed flatter and more saturated,
// and a fine paper grain sits over everything. Outlines are thick black ink
// (each piece's own thickness, so big shapes get heavier lines than small
// details), and park creases are drawn strong. All of it is in each
// material's shader, so there's no post chain: cost low, same on Low and High.

/** How lit a pixel is, as a multiple of its own color, where each band starts. */
const SHADOW = 0.42;
const MID = 0.8;
const HIGHLIGHT = 2.2;
/** Each band's brightness, times the surface color. */
const BANDS = { shadow: 0.55, mid: 0.85, lit: 1.05 };
/** Saturation push (1 keeps the color). */
const SATURATION = 1.35;
/** The halftone screen's cell size in pixels. */
const DOT = 5;

/**
 * The comic look in a standard material's shader: banded light, halftone
 * shadows, a hard highlight, saturated color and paper grain.
 */
export function comicShader(shader: { fragmentShader: string }): void {
  shader.fragmentShader = splice(
    shader.fragmentShader,
    "#include <opaque_fragment>",
    `{
        const vec3 comicLuma = vec3(0.299, 0.587, 0.114);
        vec3 comicBase = max(diffuseColor.rgb, vec3(0.02));
        // How lit this pixel is next to its own unlit color, then stepped into bands.
        float comicLit = dot(outgoingLight, comicLuma) / max(dot(comicBase, comicLuma), 0.02);
        float comicBand = comicLit < ${SHADOW.toFixed(2)} ? ${BANDS.shadow.toFixed(2)} : comicLit < ${MID.toFixed(2)} ? ${BANDS.mid.toFixed(2)} : ${BANDS.lit.toFixed(2)};
        vec3 comicCol = comicBase * comicBand;
        comicCol = max(mix(vec3(dot(comicCol, comicLuma)), comicCol, ${SATURATION.toFixed(2)}), 0.0);
        if (comicLit < ${SHADOW.toFixed(2)}) {
          // Halftone: a 45-degree dot screen whose dots grow as the shadow deepens.
          vec2 comicP = mat2(0.7071, -0.7071, 0.7071, 0.7071) * gl_FragCoord.xy / ${DOT.toFixed(1)};
          float comicR = 0.18 + 0.3 * clamp(1.0 - comicLit / ${SHADOW.toFixed(2)}, 0.0, 1.0);
          if (length(fract(comicP) - 0.5) < comicR) comicCol *= 0.6;
        } else if (comicLit > ${HIGHLIGHT.toFixed(2)}) {
          comicCol = mix(comicCol, vec3(1.0), 0.4);
        }
        // Paper grain: a fine per-pixel speckle.
        float comicGrain = fract(sin(dot(floor(gl_FragCoord.xy), vec2(12.9898, 78.233))) * 43758.5453);
        outgoingLight = comicCol * (0.965 + 0.07 * comicGrain);
      }`,
    "before",
  );
}

export const comic: ArtStyle = {
  id: "comic",
  name: "Comic",
  pitch: "Cel-shaded ink: hard light bands, halftone shadows, thick black outlines, flat punchy color on paper.",
  cost: "low",

  // Flat, matte color for the bands to step; glass keeps a little shine.
  material(color, params, surface: Surface) {
    const mat = new THREE.MeshStandardMaterial({
      ...params,
      color,
      roughness: surface === "glass" ? 0.3 : 0.9,
      metalness: 0,
    });
    mat.onBeforeCompile = comicShader;
    mat.customProgramCacheKey = () => "comic";
    return mat;
  },

  outlines: { scale: 1.4, ink: 0x111111, edges: 0.75 },

  lighting: {
    sunColor: 0xffffff,
    sun: 1.1,
    hemi: 0.9,
    environment: "studio",
    envIntensity: { outdoor: 0.15, indoor: 0.15 },
  },

  sky: { clouds: true },
  fog: { near: 1, far: 1 },

  // No tone mapping: the bands are the tone curve.
  toneMapping: THREE.NoToneMapping,
  exposure: 1,

  post: { ao: null, bloom: null, grade: null, tiltShift: null, mood: null, dof: null },

  dust: null,

  resolution: null,

  // The chunky toy rider suits the ink: big shapes, big lines.
  rider: NEUTRAL_BUILD,

  // Hard-edged shadows, so they print as solid shapes.
  shadows: { mapSize: 2048, radius: 1, bias: -0.0005, normalBias: 0.04 },
};
